import * as Sentry from '@sentry/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useErrorBoundary } from 'react-error-boundary';
import { Outlet } from 'react-router';
import { useData } from '../../contexts/data-context/use-data';
import { LoadingSection } from '../../design-system/loading/loading';
import { canInitiatePayments } from '../payments/utils/capability-checks';
import { Sidenav, SideNavItem } from './components/side-nav';

const Layout = () => {
  const { data, isLoading, error } = useData();
  const { showBoundary } = useErrorBoundary();
  const { invoicePayPrototype, payrollFeature } = useFlags();

  if (isLoading) {
    return <LoadingSection text="Loading..." />;
  }

  if (!data) {
    Sentry.addBreadcrumb({
      category: 'layout',
      message: 'Layout showing error boundary due to missing data',
      level: 'error',
      data: {
        hasError: !!error,
        errorMessage: error?.message || 'undefined',
        errorType: typeof error,
        isLoading,
        dataExists: !!data,
      },
    });

    showBoundary(error || new Error('Data is undefined in Layout component'));

    return null;
  }

  const { accounts } = data;

  const accountsWithPaymentCapability = accounts.filter(canInitiatePayments);

  // TODO: Add vendors back in https://linear.app/seapoint/issue/TENG-1760/re-add-vendors-page-when-ui-is-updated
  const baseNavItems: SideNavItem[] = ['TRANSACTIONS', 'ACCOUNTS', 'PAYMENTS', 'REPORTING'];
  const navItems: SideNavItem[] = baseNavItems;
  if (invoicePayPrototype) {
    navItems.push('INVOICE_PAY');
  }
  if (payrollFeature) {
    navItems.push('PAYROLLS');
  }
  const disabledNavItems =
    accountsWithPaymentCapability.length === 0
      ? [{ item: 'PAYMENTS' as const, label: 'You need a Seapoint account to make a payment' }]
      : [];

  return (
    <div className="flex h-screen w-full">
      <Sidenav items={navItems} disabledItems={disabledNavItems} />

      {/* Main Content Area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-seapoint-gray-background-modal">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
